import { z } from 'zod';

import { AddressSchema } from './party.js';

// ---- Warehouse ----
export const WarehouseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  code: z.string().min(1).max(32),
  name: z.string().min(1).max(200),
  address: AddressSchema.nullable(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Warehouse = z.infer<typeof WarehouseSchema>;

export const WarehouseCreateSchema = z.object({
  code: z.string().min(1).max(32),
  name: z.string().min(1).max(200),
  address: AddressSchema.optional(),
});
export type WarehouseCreate = z.infer<typeof WarehouseCreateSchema>;

export const WarehouseUpdateSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  address: AddressSchema.nullable().optional(),
  isActive: z.boolean().optional(),
});
export type WarehouseUpdate = z.infer<typeof WarehouseUpdateSchema>;

// ---- Stock level ----
export const StockLevelSchema = z.object({
  itemId: z.string().uuid(),
  itemCode: z.string(),
  itemName: z.string(),
  warehouseId: z.string().uuid(),
  warehouseCode: z.string(),
  warehouseName: z.string(),
  quantity: z.string().regex(/^-?\d+(\.\d{1,4})?$/),
  avgCost: z.string().regex(/^-?\d+(\.\d{1,4})?$/),
  avgCostCurrencyCode: z.string().length(3).regex(/^[A-Z]{3}$/).nullable(),
  updatedAt: z.string().datetime(),
  // Extra descriptive columns the stock page can show and filter on. Optional so
  // every existing consumer (the item detail tab, the adjust dialog) is unaffected.
  itemBarcode: z.string().nullable().optional(),
  categoryName: z.string().nullable().optional(),
  brandName: z.string().nullable().optional(),
});
export type StockLevel = z.infer<typeof StockLevelSchema>;

// ---- Stock totals + the stock report ----

/**
 * Stock value in ONE currency: Σ quantity × average cost over the lines costed
 * in it. Values are never added across currencies — a shop costs most goods in
 * USD and a few in IQD, and a sum of the two numbers means nothing. Where one
 * figure is wanted, it is converted explicitly (StockReport.converted).
 */
export interface StockValueByCurrency {
  currencyCode: string;
  /** Σ quantity × avg cost — the same arithmetic as the stock page's value column. */
  value: string;
  /** Σ quantity over the lines costed in this currency. */
  quantity: string;
  lines: number;
}

/**
 * Totals over EVERY stock line matching a filter — not one page of it. Backs the
 * stock page's footer (same filters as the list) and the stock report.
 */
export interface StockSummary {
  /** item × warehouse lines. */
  lines: number;
  /** Distinct products among them. */
  items: number;
  /** Distinct products with something on hand (quantity > 0 in some line). */
  itemsInStock: number;
  /** Σ quantity, in each product's base unit. */
  quantity: string;
  inStockLines: number;
  /** 0 < quantity ≤ lowThreshold. */
  lowLines: number;
  outOfStockLines: number;
  negativeLines: number;
  /**
   * Lines with stock ON HAND (quantity > 0) but no cost basis: their value is
   * unknown and counted nowhere. A line at zero is not one — its cost basis is
   * cleared by design when it empties.
   */
  noCostLines: number;
  /** Their quantity — stock on the shelf that the value does not cover. */
  noCostQuantity: string;
  lowThreshold: string;
  values: StockValueByCurrency[];
}

/** One row of a breakdown (a warehouse, a category, a brand). */
export interface StockReportGroup {
  /** null = "none" (a product with no category / brand). */
  id: string | null;
  code: string | null;
  name: string | null;
  lines: number;
  items: number;
  quantity: string;
  /** Lines with stock on hand but no cost (see StockSummary.noCostLines). */
  noCostLines: number;
  values: StockValueByCurrency[];
  /** The values converted into the base currency, or null when a rate is missing. */
  converted: string | null;
}

export interface StockReportLine {
  itemId: string;
  itemCode: string;
  itemName: string;
  warehouseId: string;
  warehouseName: string;
  quantity: string;
  avgCost: string;
  currencyCode: string;
  value: string;
  /** In the base currency, or null when a rate is missing. */
  converted: string | null;
}

export interface StockReport {
  generatedAt: string;
  baseCurrencyCode: string;
  summary: StockSummary;
  /**
   * Every currency's value turned into the base currency at the latest rate —
   * the single "what is it all worth" figure. `rates` says which rate was used
   * for each currency; a currency with no rate is listed in `missing` and left
   * out of `value` (so `value` is then a floor, and the page says so).
   */
  converted: {
    value: string;
    rates: Array<{ currencyCode: string; rate: string; asOf: string }>;
    missing: string[];
  };
  byWarehouse: StockReportGroup[];
  byCategory: StockReportGroup[];
  byBrand: StockReportGroup[];
  /** The most valuable lines, by converted value. */
  topByValue: StockReportLine[];
}

export const StockReportQuerySchema = z.object({
  warehouseId: z.string().uuid().optional(),
  categoryId: z.string().uuid().optional(),
  brandId: z.string().uuid().optional(),
  lowThreshold: z.string().regex(/^\d+(\.\d{1,4})?$/).optional(),
});
export type StockReportQuery = z.infer<typeof StockReportQuerySchema>;

// ---- Stock movement / adjustment ----
export const MovementTypeSchema = z.enum([
  'receipt', 'shipment', 'adjustment', 'transfer_in', 'transfer_out',
]);
export type MovementType = z.infer<typeof MovementTypeSchema>;

/**
 * GET /stock/movements filters. Every one is optional, so the plain call the
 * Stock movements page makes still lists everything, newest first.
 *
 * `type` takes a movement type, or `in` / `out` for every movement that ADDED /
 * REMOVED stock whatever caused it — a returned line is a receipt that went in,
 * an emptied level is an adjustment that went out, and "what came in" is the
 * question a keeper actually asks. `from` / `to` bound `occurred_at` (inclusive).
 */
export const StockMovementsQuerySchema = z.object({
  itemId: z.string().uuid().optional(),
  warehouseId: z.string().uuid().optional(),
  type: z.union([MovementTypeSchema, z.enum(['in', 'out'])]).optional(),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  limit: z.coerce.number().int().min(1).max(1000).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});
export type StockMovementsQuery = z.infer<typeof StockMovementsQuerySchema>;

/** One row of the movement log. */
export interface StockMovement {
  id: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  warehouseId: string;
  warehouseCode: string;
  warehouseName: string;
  movementType: MovementType;
  /** Signed, in the item's BASE unit: positive came in, negative went out. */
  quantity: string;
  unitCost: string | null;
  unitCostCurrencyCode: string | null;
  /** What caused this move — e.g. the bill that received it, or an import batch. */
  referenceType: string | null;
  referenceId: string | null;
  /** The causing document's number (invoice, bill, transfer, production order), when it has one. */
  referenceNumber: string | null;
  /** Who did it; null for the system or a user since removed. */
  actorName: string | null;
  occurredAt: string;
  note: string | null;
  /**
   * Stock on hand right AFTER this movement, across the warehouses the list
   * covers (one, when filtered to a warehouse). Only for a single item — a
   * balance across products is not a number — so null without `itemId`.
   */
  balanceAfter: string | null;
}

export interface StockMovementList {
  items: StockMovement[];
  /** Movements matching every filter, not just this page. */
  total: number;
  /** In / out over EVERY movement matching the filters, not one page of them. */
  totals: { in: string; out: string; net: string };
  /** With `itemId`: what is on hand now, over the same warehouses. */
  onHand: string | null;
}

/**
 * One warehouse's share of ONE product — the item profile's "how much, where".
 * Every active warehouse the viewer may see is listed, holding stock or not, so
 * a zero reads as a zero rather than as a branch that was forgotten.
 */
export interface ItemStockWarehouse {
  warehouseId: string;
  code: string;
  name: string;
  isActive: boolean;
  /** Signed — a negative is real (sold ahead of a receipt) and must show. */
  quantity: string;
  avgCost: string | null;
  avgCostCurrencyCode: string | null;
  lastMovementAt: string | null;
  lastInAt: string | null;
  lastOutAt: string | null;
  /** Base units in / out over the summary's window. */
  periodIn: string;
  periodOut: string;
  periodMovements: number;
}

export interface ItemStockSummary {
  itemId: string;
  /** The window the period figures cover, ending now. */
  days: number;
  since: string;
  warehouses: ItemStockWarehouse[];
  totals: {
    quantity: string;
    periodIn: string;
    periodOut: string;
    lastMovementAt: string | null;
    /** Warehouses below zero. */
    negativeWarehouses: number;
  };
  /** Σ quantity × average cost per currency — never added across currencies. */
  values: Array<{ currencyCode: string; value: string }>;
}

/**
 * Set a product's purchase cost (its weighted-average cost basis, per BASE unit)
 * directly — the item form's «سعر الشراء». Every warehouse holding the item when
 * no warehouse is named. The quantity is untouched; it is a price correction.
 */
export const StockCostBasisSchema = z.object({
  itemId: z.string().uuid(),
  unitCost: z.string().regex(/^\d+(\.\d{1,4})?$/),
  currencyCode: z.string().length(3).regex(/^[A-Z]{3}$/),
  warehouseId: z.string().uuid().optional(),
});
export type StockCostBasisInput = z.infer<typeof StockCostBasisSchema>;

export const StockAdjustmentSchema = z.object({
  itemId: z.string().uuid(),
  warehouseId: z.string().uuid(),
  /** Signed quantity. Positive = receipt-like, negative = shipment-like. */
  quantity: z.string().regex(/^-?\d+(\.\d{1,4})?$/),
  /** Required for inbound moves; cost dispatches at current avg for outbound. */
  unitCost: z.string().regex(/^\d+(\.\d{1,4})?$/).optional(),
  unitCostCurrencyCode: z.string().length(3).regex(/^[A-Z]{3}$/).optional(),
  movementType: MovementTypeSchema.default('adjustment'),
  occurredAt: z.string().datetime().optional(),
  note: z.string().max(2000).optional(),
});
export type StockAdjustment = z.infer<typeof StockAdjustmentSchema>;

// ---- Inventory settings (the tenant `inventory` settings key) ----

/**
 * A phone app's own answer to "may a sale drive stock below zero?".
 *
 *  - `inherit`    — follow the shop-wide setting (the default, and what every
 *                   tenant has until they touch the selector).
 *  - `allow`      — sell on minus here even when the shop-wide rule refuses:
 *                   nothing is hidden and nothing is blocked.
 *  - `block_hide` — refuse to oversell here AND drop items with nothing on hand
 *                   out of the app, which is the whole point of the override:
 *                   a phone shelf that only shows what can actually be sold.
 *
 * There is deliberately no "block but keep showing" mode per app — the shop-wide
 * pair (allowNegativeStock + hideZeroStock) still expresses that, and offering it
 * per app would promise the rep app a block its order-taking path does not apply.
 */
export const AppStockModeSchema = z.enum(['inherit', 'allow', 'block_hide']);
export type AppStockMode = z.infer<typeof AppStockModeSchema>;

/**
 * An app's own answer to the two rules SEPARATELY — the same pair the shop has.
 *
 * The three modes above bundle them: `block_hide` means "refuse AND hide", and
 * there was no way to say "refuse but keep showing" for one app, or "show
 * everything but still refuse", even though the shop level has always been two
 * independent checkboxes. A field left out here follows the shop for that rule
 * alone, so an app can override one and inherit the other.
 */
export const AppStockRulesSchema = z.object({
  allowNegative: z.boolean().optional().catch(undefined),
  hideZeros: z.boolean().optional().catch(undefined),
});
export type AppStockRules = z.infer<typeof AppStockRulesSchema>;

/** What may be stored per app: the legacy mode word, or the explicit pair. */
export const AppStockSettingSchema = z.union([AppStockModeSchema, AppStockRulesSchema]);
export type AppStockSetting = z.infer<typeof AppStockSettingSchema>;

/**
 * The value stored under the `inventory` settings key. Every field is optional,
 * and every one of them FALLS BACK rather than failing: this object decides
 * whether a shop may sell, so one unreadable field (an older writer, a mode this
 * build doesn't know) must never discard the fields around it — least of all the
 * guard itself, whose loss would quietly re-allow overselling.
 */
export const InventorySettingsSchema = z.object({
  /** Shop-wide "sell on minus". Missing = true (allow), the historical default. */
  allowNegativeStock: z.boolean().optional().catch(undefined),
  /** Whether the guard ALSO hides empty shelves. Missing = hide. */
  hideZeroStock: z.boolean().optional().catch(undefined),
  /** THE tenant default warehouse; null/missing = first active warehouse by code. */
  defaultWarehouseId: z.string().uuid().nullable().optional().catch(undefined),
  /**
   * Hide documents that belong to NO branch from users confined to one.
   *
   * Branch scope shows a restricted user their own warehouses OR rows with no
   * warehouse at all, so that history written before branches existed does not
   * vanish the day somebody is scoped. That is right while a shop is migrating
   * and wrong once it has finished: a shop whose old records are all unbranched
   * shows its branch staff almost everything, which is the opposite of scoping.
   *
   * Missing = false = that original behaviour, so no shop changes underneath it.
   * Owners and other unrestricted users are unaffected either way — they have no
   * scope to narrow.
   */
  branchStrict: z.boolean().optional().catch(undefined),
  /** Per-phone-app overrides of the two rules above. */
  apps: z
    .object({
      /** The customer shopping app (requests carrying `X-Client: mobile`). */
      customer: AppStockSettingSchema.optional().catch(undefined),
      /** The mandob field surface (rep app + the /field workspace). */
      mandob: AppStockSettingSchema.optional().catch(undefined),
    })
    .optional()
    .catch(undefined),
});
export type InventorySettings = z.infer<typeof InventorySettingsSchema>;

/** Which surface is asking. `default` = the shop-wide rule (web, register, till). */
export type StockSurface = 'default' | 'customer_app' | 'mandob_app';

/** The two questions every selling surface asks about stock. */
export interface StockPolicy {
  /** May a sale here drive on-hand below zero? */
  allowNegative: boolean;
  /** Do items with nothing on hand disappear from this surface? */
  hideZeros: boolean;
}

/** Read the stored settings value (MySQL JSON arrives as an object OR a string). */
export function parseInventorySettings(raw: unknown): InventorySettings {
  const obj = typeof raw === 'string' ? safeJson(raw) : raw;
  const parsed = InventorySettingsSchema.safeParse(obj ?? {});
  return parsed.success ? parsed.data : {};
}

function safeJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return {};
  }
}

/**
 * The single place the stock rules are decided, for every surface.
 *
 * Hiding is only meaningful while the guard refuses to oversell: with selling on
 * minus allowed everything is sellable, so nothing hides. A missing `hideZeroStock`
 * means hide — what the storefront always did under the guard, so no shop changes
 * behaviour until it touches a toggle.
 */
export function resolveStockPolicy(raw: unknown, surface: StockSurface = 'default'): StockPolicy {
  const v = parseInventorySettings(raw);
  const inherited: StockPolicy = {
    allowNegative: v.allowNegativeStock !== false,
    hideZeros: v.allowNegativeStock === false && v.hideZeroStock !== false,
  };
  const mode =
    surface === 'customer_app' ? v.apps?.customer
    : surface === 'mandob_app' ? v.apps?.mandob
    : undefined;
  if (!mode || mode === 'inherit') return inherited;
  if (mode === 'allow') return { allowNegative: true, hideZeros: false };
  if (mode === 'block_hide') return { allowNegative: false, hideZeros: true };
  // The explicit pair: each rule answered on its own, and an unanswered one
  // still follows the shop — that is what makes them separable rather than a
  // second bundle.
  return {
    allowNegative: mode.allowNegative ?? inherited.allowNegative,
    hideZeros: mode.hideZeros ?? inherited.hideZeros,
  };
}
